import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Upload, FileText, TrendingUp, TrendingDown, DollarSign, Users, 
  BarChart3, Loader2, ArrowLeft, Trash2
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, Cell
} from 'recharts';
import PeriodFilter from '../components/PeriodFilter';
import AskRootyButton from '../components/AskRootyButton';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  current: '#f97316',
  lastYear: '#94a3b8',
  positive: '#22c55e',
  negative: '#ef4444',
};

const formatCurrency = (val) => {
  if (!val && val !== 0) return '$0';
  return '$' + Number(val).toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const formatPct = (val) => {
  if (!val && val !== 0) return '0%';
  return Number(val).toFixed(1) + '%';
};

export default function FinancialReports() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('current_week');
  const fileInputRef = useRef(null);

  useEffect(() => { fetchReports(); }, [period]);

  const fetchReports = async () => {
    try {
      const res = await axios.get(`${API_URL}/reports/executive-summaries`, { params: { period } });
      const list = res.data || [];
      setReports(list);
      setSelectedReport(prev => (prev && list.some(r => r.id === prev.id)) ? prev : (list[0] || null));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are accepted');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/reports/upload-executive-summary`, formData, { timeout: 120000 });
      toast.success(res.data.message);
      await fetchReports();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || 'Failed to parse PDF. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (reportId) => {
    try {
      await axios.delete(`${API_URL}/reports/${reportId}`);
      toast.success('Report deleted');
      if (selectedReport?.id === reportId) setSelectedReport(null);
      fetchReports();
    } catch { toast.error('Failed to delete'); }
  };

  const report = selectedReport;
  const stores = report?.stores || [];
  const info = report?.report_info || {};
  const totals = report?.totals || {};

  // Build chart data for Net Sales comparison
  const salesChartData = stores.map(s => ({
    name: s.store_id || s.store_name?.slice(0, 12),
    fullName: s.store_name,
    current: s.current_year?.net_sales || s.current_period?.net_sales || 0,
    lastYear: s.last_year?.net_sales || s.comparison_period?.net_sales || 0,
  }));

  // Build chart data for cost % comparison
  const costChartData = stores.map(s => ({
    name: s.store_id || s.store_name?.slice(0, 12),
    fullName: s.store_name,
    foodCurrent: s.current_year?.food_cost_pct || 0,
    foodLast: s.last_year?.food_cost_pct || 0,
    labourCurrent: s.current_year?.labour_pct || s.current_period?.labour_pct || 0,
    labourLast: s.last_year?.labour_pct || s.comparison_period?.labour_pct || 0,
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const store = salesChartData.find(s => s.name === label);
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border text-sm">
        <p className="font-semibold text-slate-800 mb-1">{store?.fullName || label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>
            {p.name}: {typeof p.value === 'number' && p.value > 100 ? formatCurrency(p.value) : formatPct(p.value)}
          </p>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-3" style={{ fontFamily: 'Chivo, sans-serif' }}>Financial Reports</h1>
            <p className="text-white/80 text-sm mt-1">Financial data across all stores</p>
          </div>
          <div>
            <input ref={fileInputRef} type="file" accept=".pdf" className="hidden" onChange={handleUpload} />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-white text-orange-600 hover:bg-orange-50 rounded-sm font-semibold"
              data-testid="upload-exec-summary-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              {uploading ? 'Parsing PDF...' : 'Upload Executive Summary'}
            </Button>
          </div>
        </div>
      </div>

      {/* Period filter + Ask Rooty */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <PeriodFilter value={period} onChange={setPeriod} />
        <AskRootyButton
          label="Ask Rooty about this period"
          dataTestId="ask-rooty-financials"
          contextPrompt={`Analyze our A&W financial performance for the period: ${period.replace('_', ' ')}. Look at the most recent financial report data we have. Highlight: (1) top performing store, (2) any store flagging on labour, food cost, or service time, (3) one specific action item the operations team should focus on this week. Be concise, use bullet points, and cite specific numbers.`}
        />
      </div>

      {/* Report selector */}
      {reports.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {reports.map(r => (
            <button
              key={r.id}
              onClick={() => setSelectedReport(r)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                selectedReport?.id === r.id
                  ? 'bg-orange-500 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-orange-50'
              }`}
            >
              <FileText className="w-4 h-4" />
              {r.report_info?.current_period || `Week ${r.report_info?.week_number || '?'} ${r.report_info?.current_year || r.report_info?.year || ''}`}
              {r.report_info?.week_ending && <span className="opacity-70 text-xs">· {r.report_info.week_ending}</span>}
            </button>
          ))}
        </div>
      )}

      {!report ? (
        <Card className="border-0 shadow-lg rounded-2xl" data-testid="financial-empty-state">
          <CardContent className="p-12 text-center">
            <BarChart3 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-bold text-slate-700 mb-2">{period === 'current_week' ? 'No Reports Yet' : 'No reports for this period'}</h3>
            <p className="text-slate-500 mb-4">
              {period === 'current_week'
                ? 'Upload a QSS Executive Summary PDF to see financial comparisons'
                : `No executive summary falls in “${period.replace('_', ' ')}”. Try another period or upload a report.`}
            </p>
            {period !== 'current_week' && (
              <Button variant="outline" onClick={() => setPeriod('current_week')} className="rounded-xl" data-testid="financial-period-reset-btn">Back to Current Week</Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Report Info */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Week {info.week_number} — {info.current_year || info.year || 'Current'} vs {info.comparison_year || 'Last Year'}
              </h2>
              <p className="text-sm text-slate-500">{info.comparison_period || 'Same Dates Last Year'} | {stores.length} stores</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(report.id)}
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          </div>

          {/* Net Sales Comparison Bar Chart */}
          <Card className="border-0 shadow-lg rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                <DollarSign className="w-5 h-5 text-green-600" />
                Net Sales by Store — {info.current_year || 'Current'} vs {info.comparison_year || 'Last Year'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={salesChartData} margin={{ top: 10, right: 20, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="current" name={`${info.current_year || 'Current'}`} fill={COLORS.current} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="lastYear" name={`${info.comparison_year || 'Last Year'}`} fill={COLORS.lastYear} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Labour & Food Cost % Comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-0 shadow-lg rounded-2xl">
              <CardHeader className="pb-2">
                <CardTitle className="text-base" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Food Cost % by Store
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={costChartData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="foodCurrent" name={`${info.current_year || 'Current'}`} fill="#f97316" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="foodLast" name={`${info.comparison_year || 'Last Year'}`} fill="#cbd5e1" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg rounded-2xl">
              <CardHeader className="pb-2">
                <CardTitle className="text-base" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Labour Cost % by Store
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={costChartData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="labourCurrent" name={`${info.current_year || 'Current'}`} fill="#3b82f6" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="labourLast" name={`${info.comparison_year || 'Last Year'}`} fill="#cbd5e1" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Store Cards with Details */}
          <h3 className="text-lg font-bold text-slate-800 mt-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Store Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stores.map((store, i) => {
              const cy = store.current_year || store.current_period || {};
              const ly = store.last_year || store.comparison_period || {};
              const salesDiff = (cy.net_sales || 0) - (ly.net_sales || 0);
              const salesPct = ly.net_sales ? ((salesDiff / ly.net_sales) * 100).toFixed(1) : '0';
              const isUp = salesDiff >= 0;

              return (
                <Card key={i} className="border-0 shadow-md rounded-xl overflow-hidden">
                  <div className={`h-1 ${isUp ? 'bg-green-500' : 'bg-red-500'}`} />
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-bold text-slate-800 text-sm">{store.store_id} - {store.store_name}</p>
                      </div>
                      <Badge className={isUp ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                        {isUp ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                        {isUp ? '+' : ''}{salesPct}%
                      </Badge>
                    </div>

                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-500">Net Sales</span>
                        <div className="text-right">
                          <span className="font-bold text-slate-800">{formatCurrency(cy.net_sales)}</span>
                          <span className="text-slate-400 ml-2">vs {formatCurrency(ly.net_sales)}</span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-500">Customers</span>
                        <div className="text-right">
                          <span className="font-bold text-slate-800">{(cy.customer_count || 0).toLocaleString()}</span>
                          <span className="text-slate-400 ml-2">vs {(ly.customer_count || 0).toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-500">Food Cost</span>
                        <div className="text-right">
                          <span className="font-bold text-slate-800">{formatPct(cy.food_cost_pct)}</span>
                          <span className="text-slate-400 ml-2">vs {formatPct(ly.food_cost_pct)}</span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-500">Labour</span>
                        <div className="text-right">
                          <span className="font-bold text-slate-800">{formatPct(cy.labour_pct)}</span>
                          <span className="text-slate-400 ml-2">vs {formatPct(ly.labour_pct)}</span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-500">Paper Cost</span>
                        <div className="text-right">
                          <span className="font-bold text-slate-800">{formatPct(cy.paper_cost_pct)}</span>
                          <span className="text-slate-400 ml-2">vs {formatPct(ly.paper_cost_pct)}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
